import {FormEvent, useEffect, useState} from "react";
import {Alert, Card, Form} from "react-bootstrap";
import {ProductsApi} from "../../../api/ProductsApi";
import {
    Architecture, Platform, PlatformArchitectures, PlatformFormats, PlatformRequirements, Platforms, PluginFormat,
    ProductRequirements, ProductViewModel, REQUIREMENT_TEXT_MAX_LENGTH
} from "../../../api/models";
import {ErrorAlert, fieldError} from "../../../components/ErrorAlert";
import {SubmitButton} from "../../../components/SubmitButton";
import {Field, FieldControl} from "../../../components/Field";
import {architectureName, gapLines, hasRequirements, isMobile, requirementGaps} from "../../../components/requirements";
import {products} from "../../../i18n";

const names = products.requirements;

type PlatformRow = PlatformRequirements & { enabled: boolean };
type Texts = Pick<ProductRequirements, 'processor' | 'memory' | 'disk' | 'other' | 'notAvailable'>;

const placeholders: Record<Platform, string> = {
    windows: 'Windows 10 (64-bit) or later',
    macos: 'macOS 11 Big Sur or later',
    linux: 'Ubuntu 22.04 or equivalent (glibc 2.35)',
    ios: 'iOS 16 or later',
    ipados: 'iPadOS 16 or later',
    android: 'Android 10 or later'
};

const TEXT_FIELDS: { key: keyof Texts, label: string, placeholder: string, help?: string }[] = [
    { key: 'processor', label: 'Processor', placeholder: '64-bit CPU with SSE4.1, or Apple silicon' },
    { key: 'memory', label: 'Memory', placeholder: '4 GB RAM (8 GB recommended)' },
    { key: 'disk', label: 'Disk space', placeholder: '300 MB' },
    { key: 'other', label: 'Also needs', placeholder: 'An internet connection to activate' },
    { key: 'notAvailable', label: 'Also not available', placeholder: 'No 32-bit version',
        help: "Only what the list below doesn't already say: it's worked out from the platforms." }
];

// One row for each platform, on or off; a platform with a single possible architecture always has it.
function toRows(requirements: ProductRequirements | null | undefined): Record<Platform, PlatformRow> {
    return Object.fromEntries(Platforms.map(platform => {
        const saved = requirements?.platforms.find(x => x.platform === platform);
        const single = PlatformArchitectures[platform].length === 1 ? [...PlatformArchitectures[platform]] : [];
        return [platform, {
            platform, enabled: !!saved, minimumVersion: saved?.minimumVersion ?? '', devices: saved?.devices ?? '', storeUrl: saved?.storeUrl ?? '',
            architectures: saved?.architectures.length ? saved.architectures : single, formats: saved?.formats ?? []
        }];
    })) as Record<Platform, PlatformRow>;
}

const toTexts = (requirements: ProductRequirements | null | undefined): Texts => ({
    processor: requirements?.processor ?? '', memory: requirements?.memory ?? '', disk: requirements?.disk ?? '',
    other: requirements?.other ?? '', notAvailable: requirements?.notAvailable ?? ''
});

function toRequirements(rows: Record<Platform, PlatformRow>, texts: Texts): ProductRequirements {
    return {
        platforms: Platforms.filter(x => rows[x].enabled).map(x => {
            const {enabled, ...row} = rows[x];
            return {
                ...row, minimumVersion: row.minimumVersion.trim(), devices: row.devices.trim(),
                storeUrl: isMobile(x) ? row.storeUrl.trim() : ''
            };
        }),
        processor: texts.processor.trim(), memory: texts.memory.trim(), disk: texts.disk.trim(),
        other: texts.other.trim(), notAvailable: texts.notAvailable.trim()
    };
}

const toggle = <T, >(list: T[], value: T, on: boolean): T[] => on ? [...list.filter(x => x !== value), value] : list.filter(x => x !== value);

const isHttpsUrl = (value: string) => {
    try {
        return new URL(value).protocol === 'https:';
    } catch {
        return false;
    }
};

// The product's System requirements, for its page: which platforms, with their formats and processors, and what
// every platform needs. Saved on their own (the product's PUT takes just them).
export function ProductRequirementsCard({product, onSaved}: { product: ProductViewModel, onSaved(product: ProductViewModel): void }) {
    const [rows, setRows] = useState(() => toRows(product.requirements));
    const [texts, setTexts] = useState(() => toTexts(product.requirements));
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<unknown>();
    const [sources, setSources] = useState<ProductViewModel[]>([]);
    const [copiedFrom, setCopiedFrom] = useState<ProductViewModel>();

    useEffect(() => {
        setRows(toRows(product.requirements));
        setTexts(toTexts(product.requirements));
        setCopiedFrom(undefined);
    }, [product]);

    // The other products that have requirements, to copy from. Without the list there's just no menu.
    useEffect(() => {
        let current = true;
        ProductsApi.getAll()
            .then(all => current && setSources(all
                .filter(x => x.id !== product.id && hasRequirements(x.requirements))
                .sort((a, b) => a.name.localeCompare(b.name))))
            .catch(() => current && setSources([]));
        return () => { current = false; };
    }, [product.id]);

    // Fills the form in from another product, to look over and save; store links are that product's own, so they stay.
    const copyFrom = (id: string) => {
        const source = sources.find(x => x.id === id);
        if (!source) {
            return;
        }
        const copied = toRows(source.requirements);
        setRows(current => Object.fromEntries(Platforms.map(x => [x, { ...copied[x], storeUrl: current[x].storeUrl }])) as Record<Platform, PlatformRow>);
        setTexts(toTexts(source.requirements));
        setCopiedFrom(source);
        setSaved(false);
        setValidated(false);
    };

    const requirements = toRequirements(rows, texts);
    const gaps = requirementGaps(requirements);
    const preview = [...gapLines(gaps).map(x => `${x.label}: ${x.text}`), ...(gaps.notAvailable ? [gaps.notAvailable] : [])];
    const setRow = (platform: Platform, changes: Partial<PlatformRow>) => setRows(x => ({ ...x, [platform]: { ...x[platform], ...changes } }));
    const storeProblem = (row: PlatformRow) => row.enabled && isMobile(row.platform) && !!row.storeUrl.trim() && !isHttpsUrl(row.storeUrl.trim());

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (Platforms.some(x => storeProblem(rows[x]))) {
            return;
        }
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            // Nothing entered takes them away, so the page has no section.
            onSaved(await ProductsApi.update(product.id, { requirements: hasRequirements(requirements) ? requirements : null }));
            setSaved(true);
            setValidated(false);
            setCopiedFrom(undefined);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="mt-4">
            <Card.Header>System requirements</Card.Header>
            <Card.Body>
                <ErrorAlert error={error}/>
                {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Saved.</Alert>}
                <p className="small text-body-secondary">
                    Shown on the product's page, with what isn't available worked out from what's ticked here.
                </p>
                {sources.length > 0 && (
                    <Field className="mb-3" controlId="requirements-copy" label="Copy from another product"
                           help={copiedFrom ? `Copied from ${copiedFrom.name}, apart from store links. Nothing is saved until you save.` : undefined}>
                        <Form.Select value="" onChange={e => copyFrom(e.target.value)}>
                            <option value="">Choose a product…</option>
                            {sources.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                        </Form.Select>
                    </Field>
                )}
                <Form noValidate onSubmit={submit}>
                    {Platforms.map(platform => {
                        const row = rows[platform];
                        const id = `requirements-${platform}`;
                        const architectures = PlatformArchitectures[platform];
                        return (
                            <fieldset key={platform} className="border rounded p-3 mb-3">
                                <legend className="visually-hidden">{names.platforms[platform]}</legend>
                                <Form.Check type="switch" id={id} label={<strong>{names.platforms[platform]}</strong>}
                                            checked={row.enabled} onChange={e => setRow(platform, { enabled: e.target.checked })}/>
                                {row.enabled && (
                                    <div className="mt-3">
                                        <Field className="mb-3" controlId={`${id}-version`} label="Minimum version">
                                            <FieldControl value={row.minimumVersion} placeholder={placeholders[platform]} maxLength={REQUIREMENT_TEXT_MAX_LENGTH}
                                                          onChange={e => setRow(platform, { minimumVersion: e.target.value })}/>
                                        </Field>
                                        <div className="mb-3" role="group" aria-labelledby={`${id}-architectures`}>
                                            <div id={`${id}-architectures`} className="form-label">Processors</div>
                                            {architectures.map((a: Architecture) => (
                                                <Form.Check key={a} inline id={`${id}-${a}`} label={architectureName(platform, a)}
                                                            disabled={architectures.length === 1}
                                                            checked={row.architectures.includes(a)}
                                                            onChange={e => setRow(platform, { architectures: toggle(row.architectures, a, e.target.checked) })}/>
                                            ))}
                                        </div>
                                        <div className="mb-3" role="group" aria-labelledby={`${id}-formats`}>
                                            <div id={`${id}-formats`} className="form-label">Formats</div>
                                            {PlatformFormats[platform].map((f: PluginFormat) => (
                                                <Form.Check key={f} inline id={`${id}-${f}`} label={names.formatNames[f]}
                                                            checked={row.formats.includes(f)}
                                                            onChange={e => setRow(platform, { formats: toggle(row.formats, f, e.target.checked) })}/>
                                            ))}
                                        </div>
                                        <Field className={isMobile(platform) ? 'mb-3' : 'mb-0'} controlId={`${id}-devices`} label="Devices (optional)">
                                            <FieldControl value={row.devices} maxLength={REQUIREMENT_TEXT_MAX_LENGTH}
                                                          placeholder={isMobile(platform) ? 'iPad with an A12 chip or later' : ''}
                                                          onChange={e => setRow(platform, { devices: e.target.value })}/>
                                        </Field>
                                        {isMobile(platform) && (
                                            <Field className="mb-0" controlId={`${id}-store`} label="Store link (optional)"
                                                   error={(validated && storeProblem(row) && 'An https:// address.') ||
                                                       fieldError(error, `requirements.platforms[${Platforms.filter(x => rows[x].enabled).indexOf(platform)}].storeUrl`)}>
                                                <FieldControl type="url" value={row.storeUrl} maxLength={REQUIREMENT_TEXT_MAX_LENGTH}
                                                              placeholder={platform === 'android' ? 'https://play.google.com/store/apps/details?id=…' : 'https://apps.apple.com/app/…'}
                                                              onChange={e => setRow(platform, { storeUrl: e.target.value })}/>
                                            </Field>
                                        )}
                                    </div>
                                )}
                            </fieldset>
                        );
                    })}

                    {TEXT_FIELDS.map(x => (
                        <Field key={x.key} className="mb-3" controlId={`requirements-${x.key}`} label={`${x.label} (optional)`} help={x.help}>
                            <FieldControl value={texts[x.key]} placeholder={x.placeholder} maxLength={REQUIREMENT_TEXT_MAX_LENGTH}
                                          onChange={e => setTexts(t => ({ ...t, [x.key]: e.target.value }))}/>
                        </Field>
                    ))}

                    <div className="mb-3">
                        <div className="form-label">The page will say isn't available</div>
                        {preview.length
                            ? <ul className="small mb-0">{preview.map(x => <li key={x}>{x}</li>)}</ul>
                            : <p className="small text-body-secondary mb-0">Nothing{requirements.platforms.length ? '' : ' (until a platform is on)'}.</p>}
                    </div>

                    <SubmitButton busy={busy}>Save requirements</SubmitButton>
                </Form>
            </Card.Body>
        </Card>
    );
}
